import { useGraphDocumentData } from "@features/builder/GraphDocumentProvider/hooks/useGraphDocumentData"
import { useNodesValidation } from "@features/builder/graph/nodes/validation/useNodesValidation"
import { GraphTagsFieldRow } from "@features/builder/NodeDrawer/NodeForm/GraphTagsFieldRow"
import { NodeFormValues } from "@features/builder/NodeDrawer/NodeForm"
import { getMissingRequiredTagErrors } from "@features/builder/NodeDrawer/NodeForm/helpers"
import { GraphDetails } from "@features/builder/types"
import { Add, Info } from "@mui/icons-material"
import { FormHelperText, Grid, IconButton, Tooltip } from "@mui/material"
import { CUSTOM_ERROR_ATTRIBUTE } from "@utils/form"
import { useMemo } from "react"
import { useFieldArray, useFormContext, useFormState } from "react-hook-form"

type GraphTagsFieldProps = {
  nodeId: string
}

export const GraphTagsField = ({ nodeId }: GraphTagsFieldProps) => {
  const form = useFormContext<NodeFormValues>()
  const { fields, append, remove } = useFieldArray({ control: form.control, name: "graph_tags" })
  const { errors } = useFormState({ control: form.control, name: "graph_tags" })
  const errorMessage = errors.graph_tags?.root?.message ?? ""

  const { getNodeValidation } = useNodesValidation()
  const validationErrors = getNodeValidation(nodeId, "GRAPH_TAGS")?.errors ?? []
  const missingRequiredTagErrors = getMissingRequiredTagErrors(validationErrors)

  const graphDocumentData = useGraphDocumentData()
  const graphData: GraphDetails | null = useMemo(() => {
    try {
      return JSON.parse(graphDocumentData.data)
    } catch {
      return null
    }
  }, [graphDocumentData.data])

  const graphTagsMap = useMemo(() => {
    const map = new Map<string, string[]>()
    ;(graphData?.GraphTags ?? []).forEach((tag) => {
      map.set(tag.Name, [...(map.get(tag.Name) ?? []), tag.Value])
    })
    return map
  }, [graphData])

  return (
    <fieldset style={{ color: "rgba(0, 0, 0, 0.6)", borderColor: "rgba(0, 0, 0, 0.6)" }}>
      <legend>
        Graph level tags
        <Tooltip
          title={`Graph level tags are tags that are defined on the product level 
            but values which can be used for these tags are defined directly on the graph level.
            You can choose multiple options for the same tag.`}
        >
          <Info fontSize="small" sx={{ verticalAlign: "bottom", ml: 1 }} />
        </Tooltip>
      </legend>
      <Grid container direction="column" spacing={1} wrap="nowrap">
        {fields.map((tagField, index) => (
          <GraphTagsFieldRow
            key={tagField.id}
            index={index}
            onDelete={() => remove(index)}
            graphTagsMap={graphTagsMap}
            validationErrors={validationErrors}
          />
        ))}

        {missingRequiredTagErrors.map((error, index) => (
          <Grid item key={index}>
            <FormHelperText error>{error.msg}</FormHelperText>
          </Grid>
        ))}

        {errorMessage && (
          <Grid item>
            <FormHelperText error {...{ [CUSTOM_ERROR_ATTRIBUTE]: true }}>
              {errorMessage}
            </FormHelperText>
          </Grid>
        )}

        <Grid item>
          <Tooltip title="Add Tag">
            <span>
              <IconButton
                color="primary"
                size="small"
                disabled={form.formState.disabled}
                onClick={() => append({ tag: "", values: [] })}
              >
                <Add />
              </IconButton>
            </span>
          </Tooltip>
        </Grid>
      </Grid>
    </fieldset>
  )
}
